import React, { useState, useEffect } from 'react';
import { FiTerminal, FiArrowLeft, FiShare2, FiCheck, FiDownload, FiExternalLink, FiCompass } from 'react-icons/fi';
import SudokuGame from './SudokuGame';

/**
 * SudokuPage — Dedicated standalone page for the Sudoku x86 Assembly Game Engine.
 * Allows direct URL sharing (/sudoku) while providing seamless navigation
 * back to the full interactive developer portfolio.
 */
function SudokuPage({ onNavigateHome }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'Sudoku Game Engine (x86 Assembly) | Shawaiz Ali Rehman';

    return () => {
      document.title = 'Shawaiz Ali Rehman | Mobile & Software Engineer Portfolio';
    };
  }, []);

  const handleShare = async () => {
    const url = window.location.origin ? `${window.location.origin}/sudoku` : 'https://shawaizalirehman.vercel.app/sudoku';
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
        return;
      } catch (err) {
        console.error('Clipboard copy failed:', err);
      }
    }

    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      console.error('ExecCommand copy failed', e);
    }
    document.body.removeChild(textarea);
  };

  const handleGoHome = (e) => {
    if (e) e.preventDefault();
    if (onNavigateHome) {
      onNavigateHome();
    } else {
      window.history.pushState(null, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  return (
    <div className="sudoku-standalone-page">
      {/* ─── STANDALONE TOP NAVIGATION ─── */}
      <header className="sudoku-navbar">
        <div className="sudoku-nav-container">
          <div className="sudoku-nav-left">
            <button className="sudoku-back-btn font-mono" onClick={handleGoHome}>
              <FiArrowLeft className="back-icon" />
              <span>Full Portfolio</span>
            </button>

            <div className="sudoku-brand" onClick={handleGoHome}>
              <FiTerminal className="brand-icon" />
              <span className="brand-text">
                <span className="brand-gold">Shawaiz</span> Ali Rehman
              </span>
            </div>
          </div>

          <div className="sudoku-nav-right">
            <button
              className={`sudoku-share-btn font-mono${copied ? ' copied' : ''}`}
              onClick={handleShare}
              title="Copy shareable link to this Sudoku game"
            >
              {copied ? (
                <>
                  <FiCheck className="share-icon" />
                  <span>Copied Link!</span>
                </>
              ) : (
                <>
                  <FiShare2 className="share-icon" />
                  <span>Share Game</span>
                </>
              )}
            </button>

            <a
              href="/resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="sudoku-resume-link font-mono"
            >
              <FiDownload size={13} />
              <span>Resume</span>
            </a>

            <a
              href="https://github.com/Shawaiz788"
              target="_blank"
              rel="noopener noreferrer"
              className="sudoku-github-link font-mono"
            >
              <span>GitHub</span>
              <FiExternalLink size={12} />
            </a>
          </div>
        </div>
      </header>

      {/* ─── WELCOME SPOTLIGHT BANNER ─── */}
      <div className="sudoku-spotlight-banner">
        <div className="banner-inner font-mono">
          <span className="banner-tag">DIRECT PROJECT LINK</span>
          <span className="banner-text">
            Playing the standalone <strong>x86 Assembly Sudoku Engine</strong>.
          </span>
          <button className="banner-cta font-mono" onClick={handleGoHome}>
            <FiCompass /> Explore Full Portfolio &amp; Story →
          </button>
        </div>
      </div>

      {/* ─── REUSED SUDOKU GAME ENGINE UI ─── */}
      <main className="sudoku-main-wrapper">
        <SudokuGame />
      </main>

      {/* ─── DISCOVERY FOOTER CTA ─── */}
      <section className="sudoku-footer-cta">
        <div className="footer-cta-card">
          <div className="cta-header font-mono">
            <span className="cta-dot" />
            ENGINEER SHOWCASE
          </div>
          <h2 className="cta-title font-display">
            Built by <span className="cta-title-accent">Shawaiz Ali Rehman</span>
          </h2>
          <p className="cta-desc">
            Computer Science student at FAST-NUCES (GPA 3.89) specializing in Mobile Engineering (React Native, Android Java), AI Healthcare BERT Transformers, and Low-Level Systems.
          </p>

          <div className="cta-project-chips">
            <span className="cta-chip font-mono">KaamKarwao (React Native / Expo 54)</span>
            <span className="cta-chip font-mono">Android Music Streaming (Java / ExoPlayer)</span>
            <span className="cta-chip font-mono">Neem-Bert-Hakeem (BERT AI / NLP)</span>
            <span className="cta-chip font-mono">Custom Camera App (VisionCamera v4)</span>
          </div>

          <div className="cta-actions">
            <button className="btn-explore-portfolio font-mono" onClick={handleGoHome}>
              <FiCompass size={16} />
              <span>Explore Interactive 12-Chapter Portfolio →</span>
            </button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="sudoku-page-footer font-mono">
        <p>© 2026 Shawaiz Ali Rehman — Mobile &amp; Software Engineer</p>
      </footer>

      <style>{`
        .sudoku-standalone-page {
          min-height: 100vh;
          background: #080a0f;
          color: #f3f4f6;
          display: flex;
          flex-direction: column;
        }

        /* Standalone Navbar */
        .sudoku-navbar {
          position: sticky;
          top: 0;
          z-index: 100;
          background: rgba(8, 10, 15, 0.92);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          padding: 0.75rem 1.5rem;
        }

        .sudoku-nav-container {
          max-width: 1100px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .sudoku-nav-left {
          display: flex;
          align-items: center;
          gap: 1.25rem;
        }

        .sudoku-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          background: rgba(212, 163, 89, 0.1);
          border: 1px solid rgba(212, 163, 89, 0.3);
          color: #d4a359;
          padding: 0.45rem 0.9rem;
          border-radius: 8px;
          font-size: 0.76rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .sudoku-back-btn:hover {
          background: rgba(212, 163, 89, 0.2);
          transform: translateX(-2px);
          border-color: #d4a359;
        }

        .back-icon {
          font-size: 0.9rem;
        }

        .sudoku-brand {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          user-select: none;
        }

        .brand-icon {
          color: #d4a359;
          font-size: 1.15rem;
        }

        .brand-text {
          font-weight: 700;
          font-size: 1.05rem;
          color: #ffffff;
          letter-spacing: -0.02em;
        }

        .brand-gold {
          color: #d4a359;
        }

        .sudoku-nav-right {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .sudoku-share-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          background: rgba(56, 189, 248, 0.1);
          border: 1px solid rgba(56, 189, 248, 0.3);
          color: #38bdf8;
          padding: 0.45rem 0.85rem;
          border-radius: 8px;
          font-size: 0.74rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .sudoku-share-btn:hover {
          background: rgba(56, 189, 248, 0.2);
          border-color: #38bdf8;
        }

        .sudoku-share-btn.copied {
          background: rgba(16, 185, 129, 0.15);
          border-color: #10b981;
          color: #10b981;
        }

        .sudoku-resume-link,
        .sudoku-github-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          color: rgba(209, 213, 219, 0.8);
          font-size: 0.74rem;
          text-decoration: none;
          padding: 0.45rem 0.75rem;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(255, 255, 255, 0.03);
          transition: all 0.2s;
        }

        .sudoku-resume-link:hover,
        .sudoku-github-link:hover {
          color: #ffffff;
          border-color: rgba(255, 255, 255, 0.2);
          background: rgba(255, 255, 255, 0.07);
        }

        /* Spotlight Banner */
        .sudoku-spotlight-banner {
          background: linear-gradient(90deg, rgba(212, 163, 89, 0.12) 0%, rgba(56, 189, 248, 0.08) 100%);
          border-bottom: 1px solid rgba(212, 163, 89, 0.2);
          padding: 0.6rem 1.5rem;
        }

        .banner-inner {
          max-width: 1100px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
          font-size: 0.74rem;
        }

        .banner-tag {
          color: #d4a359;
          background: rgba(212, 163, 89, 0.15);
          padding: 0.2rem 0.55rem;
          border-radius: 4px;
          font-weight: 700;
          font-size: 0.65rem;
          letter-spacing: 0.1em;
        }

        .banner-text {
          color: rgba(243, 244, 246, 0.9);
        }

        .banner-text strong {
          color: #ffd700;
        }

        .banner-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: transparent;
          border: none;
          color: #38bdf8;
          font-size: 0.74rem;
          font-weight: 600;
          cursor: pointer;
          padding: 0;
          transition: color 0.2s;
        }

        .banner-cta:hover {
          color: #7dd3fc;
          text-decoration: underline;
        }

        /* Main Wrapper */
        .sudoku-main-wrapper {
          flex: 1;
          width: 100%;
        }

        /* Footer CTA */
        .sudoku-footer-cta {
          padding: 3rem 1.5rem 2rem;
          background: #06080c;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
        }

        .footer-cta-card {
          max-width: 860px;
          margin: 0 auto;
          background: rgba(14, 18, 27, 0.9);
          border: 1px solid rgba(212, 163, 89, 0.25);
          border-radius: 16px;
          padding: 2.25rem;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
        }

        .cta-header {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.7rem;
          letter-spacing: 0.18em;
          color: #d4a359;
          background: rgba(212, 163, 89, 0.1);
          padding: 0.3rem 0.8rem;
          border-radius: 100px;
        }

        .cta-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a359;
          box-shadow: 0 0 6px #d4a359;
        }

        .cta-title {
          font-size: clamp(1.8rem, 4vw, 2.5rem);
          margin: 0;
          color: #ffffff;
        }

        .cta-title-accent {
          color: #d4a359;
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
        }

        .cta-desc {
          max-width: 640px;
          margin: 0;
          color: rgba(156, 163, 175, 0.9);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .cta-project-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          justify-content: center;
        }

        .cta-chip {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(209, 213, 219, 0.8);
          font-size: 0.68rem;
          padding: 0.3rem 0.65rem;
          border-radius: 6px;
        }

        .cta-actions {
          margin-top: 0.5rem;
        }

        .btn-explore-portfolio {
          background: linear-gradient(135deg, #d4a359, #b8860b);
          color: #080a0f;
          font-weight: 700;
          font-size: 0.85rem;
          letter-spacing: 0.06em;
          padding: 0.85rem 1.8rem;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          transition: all 0.2s ease;
          box-shadow: 0 6px 20px rgba(212, 163, 89, 0.35);
        }

        .btn-explore-portfolio:hover {
          background: linear-gradient(135deg, #e5b369, #c9961b);
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(212, 163, 89, 0.5);
        }

        .sudoku-page-footer {
          text-align: center;
          padding: 1.5rem;
          background: #040508;
          color: rgba(107, 114, 128, 0.6);
          font-size: 0.72rem;
          border-top: 1px solid rgba(255, 255, 255, 0.04);
        }

        @media (max-width: 768px) {
          .sudoku-nav-container {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .sudoku-nav-left {
            width: 100%;
            justify-content: space-between;
          }

          .sudoku-nav-right {
            width: 100%;
            justify-content: flex-start;
            flex-wrap: wrap;
          }

          .footer-cta-card {
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
}

export default SudokuPage;
